'use client';

import { useState, useCallback } from 'react';

interface Toast {
  id: string;
  title?: string;
  description?: string;
  variant?: 'default' | 'destructive';
}

interface ToastState {
  toasts: Toast[];
}

let toastState: ToastState = { toasts: [] };
let listeners: Array<(state: ToastState) => void> = [];

function notifyListeners() {
  listeners.forEach((listener) => listener(toastState));
}

export function toast({
  title,
  description,
  variant = 'default',
}: Omit<Toast, 'id'>) {
  const id = Math.random().toString(36).substring(2, 9);
  const newToast: Toast = { id, title, description, variant };

  toastState = {
    toasts: [...toastState.toasts, newToast],
  };
  notifyListeners();

  // Auto-dismiss after 5 seconds
  setTimeout(() => {
    toastState = {
      toasts: toastState.toasts.filter((t) => t.id !== id),
    };
    notifyListeners();
  }, 5000);

  return id;
}

export function useToast() {
  const [state, setState] = useState<ToastState>(toastState);

  useState(() => {
    listeners.push(setState);
    return () => {
      listeners = listeners.filter((l) => l !== setState);
    };
  });

  const dismiss = useCallback((id: string) => {
    toastState = {
      toasts: toastState.toasts.filter((t) => t.id !== id),
    };
    notifyListeners();
  }, []);

  return {
    toasts: state.toasts,
    toast,
    dismiss,
  };
}
